import {
  Facebook,
  Youtube,
  Linkedin,
  Instagram,
  Twitter,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ROUTING_PATH, FILTER_KEYS, PROJECT_NAME } from "../core/config/Constant";
import { useSiteConfig } from "../features/static/hooks/useSiteConfig";
import FeedbackWidget from "../features/feedback/components/FeedbackWidget";

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const handleCityClick = (cityName) => {
    window.scrollTo(0, 0);
    navigate(`${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CITY}=${encodeURIComponent(cityName)}`);
  };

  // Social + contact values are CMS-managed; appstore/playstore aren't part of that API.
  const { data: siteConfig = {} } = useSiteConfig();
  const socialLinks = {
    facebook: siteConfig.facebook || "#",
    youtube: siteConfig.youtube || "#",
    linkedin: siteConfig.linkedin || "#",
    instagram: siteConfig.instagram || "#",
    twitter: siteConfig.twitter || "#",
    appstore: "#",
    playstore: "#",
  };


  const socialIcons = [
    { icon: Facebook, link: socialLinks.facebook, label: "Facebook" },
    { icon: Instagram, link: socialLinks.instagram, label: "Instagram" },
    { icon: Youtube, link: socialLinks.youtube, label: "YouTube" },
    { icon: Linkedin, link: socialLinks.linkedin, label: "LinkedIn" },
    { icon: Twitter, link: socialLinks.twitter, label: "Twitter" },
  ];

  const companyLinks = [
    { label: "About Us", path: ROUTING_PATH.ABOUT_US },
    { label: "Contact Us", path: ROUTING_PATH.CONTACT },
    { label: "FAQ", path: ROUTING_PATH.FAQ },
    { label: "Privacy Policy", path: ROUTING_PATH.PRIVACY_POLICY },
    { label: "Terms of Service", path: ROUTING_PATH.TERMS_AND_CONDITIONS },
  ];

  const propertyLinks = [
    { label: "Properties in Delhi", city: "Delhi" },
    { label: "Properties in Mumbai", city: "Mumbai" },
    { label: "Properties in Bengaluru", city: "Bengaluru" },
    { label: "Properties in Pune", city: "Pune" },
    { label: "Properties in Hyderabad", city: "Hyderabad" },
    { label: "Properties in Chennai", city: "Chennai" },
  ];

  const quickLinks = [
    { label: "Buy Property", path: `${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CATEGORY_TYPE}=buy` },
    { label: "Rent Property", path: `${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CATEGORY_TYPE}=rent` },
    { label: "Post Property", path: ROUTING_PATH.ADD_NEW_PROPERTY },
    { label: "All Offers", path: ROUTING_PATH.ALL_OFFERS },
    { label: "Builder Projects", path: ROUTING_PATH.PROJECT_LIST },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gray-900 text-gray-300 overflow-hidden">
      {/* Decorative gradient bar at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700" />


      {/* ─── Main Footer Grid ─── */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Column 1 — Brand */}
          <div className="lg:col-span-4">
            <img
              src="/image/reelty_Logo2-white.svg"
              alt={`${PROJECT_NAME} Logo`}
              className="h-12 w-auto mb-5 object-contain"
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              Your trusted partner in finding the perfect property.
              We offer end-to-end real estate services including home loans,
              legal assistance, property valuation, and expert advice.
            </p>

            {/* Contact details (CMS-managed) */}
            {(siteConfig.emails || siteConfig.contact_number) && (
              <div className="mb-5 space-y-1.5 text-sm text-gray-400">
                {siteConfig.emails && (
                  <a href={`mailto:${siteConfig.emails}`} className="block hover:text-white transition-colors no-underline">
                    {siteConfig.emails}
                  </a>
                )}
                {siteConfig.contact_number && (
                  <a href={`tel:${siteConfig.contact_number}`} className="block hover:text-white transition-colors no-underline">
                    {siteConfig.contact_number}
                  </a>
                )}
              </div>
            )}

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              {socialIcons.map(({ icon: Icon, link, label }, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-800 text-gray-400 hover:bg-primary-600 hover:text-white transition-all duration-300"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 — Company */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.label}>
                  <span
                    onClick={() => handleNavigation(item.path)}
                    className="text-gray-400 hover:text-white text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <ChevronRight size={14} className="text-gray-600 group-hover:text-primary-500 transition-colors" />
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  <span
                    onClick={() => handleNavigation(item.path)}
                    className="text-gray-400 hover:text-white text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <ChevronRight size={14} className="text-gray-600 group-hover:text-primary-500 transition-colors" />
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Properties in India */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Properties
            </h4>
            <ul className="space-y-3">
              {propertyLinks.map((item) => (
                <li key={item.city}>
                  <span
                    onClick={() => handleCityClick(item.city)}
                    className="text-gray-400 hover:text-white text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <ChevronRight size={14} className="text-gray-600 group-hover:text-primary-500 transition-colors" />
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5 — Download App */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Get the App
            </h4>
            <div className="flex flex-col gap-3 mb-5">
              <a
                href={socialLinks.playstore}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src="/image/abc.jpg"
                  alt="Google Play"
                  className="h-10 rounded-lg object-cover hover:opacity-80 transition-opacity"
                />
              </a>
              <a
                href={socialLinks.appstore}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src="/image/apl.png"
                  alt="App Store"
                  className="h-10 rounded-lg object-cover hover:opacity-80 transition-opacity"
                />
              </a>
            </div>

            {/* QR Code */}
            <div className="flex items-center gap-3 bg-gray-800 p-3 rounded-xl">
              <img
                src="/image/qrbar.jpg"
                alt="QR Code"
                className="h-16 w-16 rounded-lg object-cover"
              />
              <p className="text-xs text-gray-400 leading-relaxed">
                Scan to download the app
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Bottom Bar ─── */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              © {currentYear} {PROJECT_NAME}. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to={ROUTING_PATH.PRIVACY_POLICY}
              className="text-xs text-gray-500 hover:text-gray-300 no-underline hover:no-underline transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to={ROUTING_PATH.TERMS_AND_CONDITIONS}
              className="text-xs text-gray-500 hover:text-gray-300 no-underline hover:no-underline transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to={ROUTING_PATH.ABOUT_US}
              className="text-xs text-gray-500 hover:text-gray-300 no-underline hover:no-underline transition-colors"
            >
              Sitemap
            </Link>
          </div>
        </div>
      </div>

      {/* ─── Feedback Widget (floating) ─── */}
      <FeedbackWidget />
    </footer>
  );
};

export default Footer;
